import { useCallback, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type { StreamEvent } from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import type { AiAgentMeetingPayload } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import { aiAgentQueryKeys } from "./queryKeyFactory";
import { useSSEStream } from "./useSSEStream";

const useCreateThreadQuery = (payload: AiAgentMeetingPayload | null, onComplete?: VoidFunction) => {
    const queryClient = useQueryClient();
    const [threadId, setThreadId] = useState("");
    const [data, setData] = useState("");
    const [messageId, setMessageId] = useState("");

    const handleStreamEvent = useCallback((event: StreamEvent) => {
        if (ParseEventStreamService.isInfoStreamEvent(event)) {
            setThreadId(event.data.threadId);
        }

        if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
            setData((prev) => prev + event.data[0].content);
            if (event.data[0].id && event.data[0].id !== messageId) {
                setMessageId(event.data[0].id);
            }
        }

        if (ParseEventStreamService.isUnknownStreamEvent(event)) {
            setData(`${event.data.error}: ${event.data.message}`);
        }
    }, []);

    const { isLoading, stop, isFetched, isFetchedAfterMount } = useSSEStream({
        enabled: !!payload,
        queryKey: aiAgentQueryKeys.generateThread(),
        payload,
        fetcher: salesApiService.aiAgent.meeting.createAIAgentThread,
        onEvent: handleStreamEvent,
        onComplete: () => {
            onComplete?.();
            void quotaQueryClient.invalidateDataCreditsState();
            void queryClient.invalidateQueries({ queryKey: aiAgentQueryKeys.threads() });
        },
    });

    return { threadId, messageId, data, isLoading, stop, isFetched, isFetchedAfterMount };
};

export default useCreateThreadQuery;
